/**
 * Leads as Prospect tasks: the mapping both ways and the first-reply clock. Which task field
 * holds what: leadConfig.ts.
 */
import type { Identifier } from "ra-core";

import { PRODUCTS, TASK_TYPE_PK, TASK_TYPES } from "./crmConfig";
import {
  FIRST_REPLY_BUSINESS_DAYS,
  LEAD_SOURCES,
  LEAD_STATUSES,
  REPLY_SOON_HOURS,
  type LeadSourceId,
  type LeadStatusId,
} from "./leadConfig";
import { parseAmount, type ZiTask } from "./mappers";

export type ZcrmLead = {
  id: Identifier;
  status: LeadStatusId;
  /** The account the lead joined (an existing client or a matched account); null = no account yet. */
  company_id: Identifier | null;
  company: string;
  person: string;
  email: string;
  mobile: string;
  office_phone: string;
  /** An ACCOUNT_KINDS value. */
  type: string;
  doors: number | null;
  /** A PRODUCTS id; null = not sure yet. */
  product: string | null;
  /** What they asked for, in their words. */
  request: string;
  source: LeadSourceId | "";
  heard: string;
  referred_by: string;
  markets: string[];
  chat_url: string;
  booked_at_booth: boolean;
  marketing_consent: boolean;
  sales_id: Identifier | null;
  reply_due: string | null;
  replied_at: string | null;
  /** The next step agreed while open; once disqualified, the reason. */
  next_step: string;
  disqualify_reason: string;
  created_at: string;
  updated_at: string;
};

/** What the intake, quick add and web form send. */
export type LeadInput = Partial<Omit<ZcrmLead, "id" | "created_at">>;

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_MS = 60 * 60 * 1000;
const DEFAULT_PRIORITY = 3;
const PHONE_SEPARATOR = " · ";
const WHERE_SEPARATOR = "|";
const MARKET_SEPARATOR = ";";

const text = (value: string | null | undefined): string => value ?? "";

export const isLeadTask = (task: ZiTask): boolean =>
  task.Action === TASK_TYPES.lead;

const statusOf = (status: string | undefined): LeadStatusId =>
  LEAD_STATUSES.find((s) => s.status === status)?.id ?? "new";

/** "heard|referred by|market;market" (PetsDesc). */
const parseWhere = (value: string | null | undefined) => {
  const [heard = "", referred = "", markets = ""] =
    text(value).split(WHERE_SEPARATOR);
  return {
    heard,
    referred_by: referred,
    markets: markets.split(MARKET_SEPARATOR).filter(Boolean),
  };
};

const formatWhere = (
  lead: Pick<ZcrmLead, "heard" | "referred_by" | "markets">,
) =>
  [lead.heard, lead.referred_by, lead.markets.join(MARKET_SEPARATOR)].join(
    WHERE_SEPARATOR,
  );

const productId = (stored: string | null | undefined) =>
  PRODUCTS.find((p) => p.stored === stored)?.id ?? null;

const productStored = (id: string | null | undefined) =>
  PRODUCTS.find((p) => p.id === id)?.stored ?? "";

const sourceId = (value: string | null | undefined): LeadSourceId | "" =>
  LEAD_SOURCES.find((s) => s.label === value || s.id === value)?.id ?? "";

export const sourceLabel = (id: string): string =>
  LEAD_SOURCES.find((s) => s.id === id)?.label ?? "";

export const taskToLead = (task: ZiTask): ZcrmLead => {
  const status = statusOf(task.Status);
  const [mobile = "", office = ""] = text(task.PhoneNumber).split(
    PHONE_SEPARATOR,
  );
  const doors = parseAmount(task.AuthorizedLimit);
  const detail = task as ZiTask & {
    EntranceInstructionDesc?: string | null;
    IsBidOnly?: boolean;
  };
  return {
    id: task.id,
    status,
    company_id: task.Property ?? null,
    company: text(task.PropertyName) || text(task.Description),
    person: text(task.TenantNames),
    email: text(task.Email),
    mobile,
    office_phone: office,
    type: text(task.EntranceInstruction),
    doors: doors > 0 ? doors : null,
    product: productId(task.Key),
    request: text(task.WorkOrderInstructions),
    source: sourceId(detail.EntranceInstructionDesc),
    ...parseWhere(task.PetsDesc),
    chat_url: text(task.Notes),
    booked_at_booth: task.Pets ?? false,
    marketing_consent: detail.IsBidOnly ?? false,
    sales_id: task.ManagedBy ?? null,
    reply_due: task.EndDate ?? null,
    replied_at: task.StartDate ?? null,
    next_step: status === "disqualified" ? "" : text(task.KeyDesc),
    disqualify_reason: status === "disqualified" ? text(task.KeyDesc) : "",
    created_at: text(task.DateCreated),
    updated_at: text(task.LastModified ?? task.DateCreated),
  };
};

/** Adds business days (Monday to Friday) to a date, keeping the time of day. */
export const addBusinessDays = (from: Date, days: number): Date => {
  const date = new Date(from);
  let left = days;
  while (left > 0) {
    date.setTime(date.getTime() + DAY_MS);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) left--;
  }
  return date;
};

/** When a new lead must have had its first reply. */
export const firstReplyDue = (createdAt: Date): Date =>
  addBusinessDays(createdAt, FIRST_REPLY_BUSINESS_DAYS);

type TaskPatch = Record<string, string | number | boolean | null>;

/** Each lead field and how it is written to the Prospect task. */
const LEAD_FIELD_WRITERS: Record<string, (lead: LeadInput) => TaskPatch> = {
  status: (lead) => ({
    Status: LEAD_STATUSES.find((s) => s.id === lead.status)?.status ?? null,
  }),
  company: (lead) => ({
    Description: text(lead.company) || text(lead.person),
  }),
  company_id: (lead) => ({
    Property: lead.company_id ? Number(lead.company_id) : null,
  }),
  person: (lead) => ({ TenantNames: text(lead.person) }),
  email: (lead) => ({ Email: text(lead.email).trim() }),
  mobile: (lead) => ({
    PhoneNumber: [text(lead.mobile), text(lead.office_phone)]
      .filter(Boolean)
      .join(PHONE_SEPARATOR),
  }),
  type: (lead) => ({ EntranceInstruction: text(lead.type) }),
  doors: (lead) => ({ AuthorizedLimit: lead.doors ? String(lead.doors) : "" }),
  product: (lead) => ({ Key: productStored(lead.product) }),
  request: (lead) => ({ WorkOrderInstructions: text(lead.request) }),
  source: (lead) => ({
    EntranceInstructionDesc: sourceLabel(text(lead.source)),
  }),
  heard: (lead) => ({
    PetsDesc: formatWhere({
      heard: text(lead.heard),
      referred_by: text(lead.referred_by),
      markets: lead.markets ?? [],
    }),
  }),
  chat_url: (lead) => ({ Notes: text(lead.chat_url) }),
  booked_at_booth: (lead) => ({ Pets: !!lead.booked_at_booth }),
  marketing_consent: (lead) => ({ IsBidOnly: !!lead.marketing_consent }),
  sales_id: (lead) => ({
    ManagedBy: lead.sales_id ? Number(lead.sales_id) : null,
  }),
  reply_due: (lead) => ({ EndDate: lead.reply_due || null }),
  replied_at: (lead) => ({ StartDate: lead.replied_at || null }),
  next_step: (lead) => ({ KeyDesc: text(lead.next_step) }),
  disqualify_reason: (lead) => ({ KeyDesc: text(lead.disqualify_reason) }),
};

/** Fields written together: one task field holds them (PhoneNumber, PetsDesc). */
const SHARED_FIELDS: Record<string, string> = {
  office_phone: "mobile",
  referred_by: "heard",
  markets: "heard",
};

const sameValue = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The task fields to PATCH for a lead edit: only what changed. */
export const leadToTaskPatch = (
  data: LeadInput,
  previous: Partial<ZcrmLead> = {},
): TaskPatch => {
  const merged = { ...previous, ...data };
  const changed = new Set<string>();
  for (const key of Object.keys(data)) {
    if (
      sameValue(data[key as keyof LeadInput], previous[key as keyof ZcrmLead])
    )
      continue;
    changed.add(SHARED_FIELDS[key] ?? key);
  }
  const patch: TaskPatch = {};
  for (const field of changed) {
    const write = LEAD_FIELD_WRITERS[field];
    if (write) Object.assign(patch, write(merged));
  }
  if ("Status" in patch && patch.Status === null) {
    throw new Error(`Unknown lead status "${data.status}"`);
  }
  // A disqualified lead's KeyDesc is its reason, an open lead's its next step.
  if (data.status === "disqualified" && "disqualify_reason" in merged) {
    patch.KeyDesc = text(merged.disqualify_reason);
  }
  return patch;
};

/**
 * The two writes that save a new lead: create takes the core task fields, edit the rest (the
 * same split as deals and calls). No account is created: zInspector's API can't yet (see
 * proposed-api-writes.md), so a lead with no match keeps its company name on the task.
 */
export const buildLeadWrites = (
  input: LeadInput,
  managerId: Identifier | undefined,
  now = new Date(),
): { create: Record<string, unknown>; edit: TaskPatch } => {
  const owner = input.sales_id ?? managerId;
  const lead: LeadInput = {
    status: "new",
    reply_due: firstReplyDue(now).toISOString(),
    ...input,
    sales_id: owner ?? null,
  };
  const {
    company: _company,
    company_id: _companyId,
    sales_id: _owner,
    ...rest
  } = lead;
  const edit = leadToTaskPatch(
    { ...rest, markets: rest.markets ?? [] },
    { status: "new" },
  );
  return {
    create: {
      Action: TASK_TYPE_PK.Prospect,
      Priority: DEFAULT_PRIORITY,
      Property: lead.company_id ? Number(lead.company_id) : undefined,
      Description: text(lead.company) || text(lead.person) || "New lead",
      ManagedBy: owner == null ? undefined : Number(owner),
      AssignedToUser: owner == null ? undefined : Number(owner),
    },
    edit,
  };
};

export type LeadClock = {
  kind: "late" | "soon" | "ok" | "done";
  label: string;
};

const relativeDays = (from: string, now: Date) => {
  const days = Math.floor(
    (new Date(now.toDateString()).getTime() -
      new Date(new Date(from).toDateString()).getTime()) /
      DAY_MS,
  );
  return days <= 0 ? "today" : days === 1 ? "yesterday" : `${days}d ago`;
};

const shortDate = (date: string) =>
  new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(
    new Date(date),
  );

/** The first-reply clock: how long until a new lead must be answered, or how late it is. */
export const leadClock = (lead: ZcrmLead, now = new Date()): LeadClock => {
  if (lead.status === "qualified") {
    return { kind: "done", label: `Converted ${shortDate(lead.updated_at)}` };
  }
  if (lead.status === "disqualified") {
    return { kind: "done", label: lead.disqualify_reason || "Disqualified" };
  }
  if (lead.status === "contacted") {
    return {
      kind: "ok",
      label: `Replied ${relativeDays(lead.replied_at || lead.updated_at, now)}`,
    };
  }
  const due = new Date(
    lead.reply_due || firstReplyDue(new Date(lead.created_at)).toISOString(),
  );
  const left = due.getTime() - now.getTime();
  if (left < 0) {
    const late = -left;
    return {
      kind: "late",
      label:
        late < DAY_MS
          ? `Overdue ${Math.max(1, Math.round(late / HOUR_MS))}h`
          : `Overdue ${Math.floor(late / DAY_MS)}d`,
    };
  }
  if (left <= REPLY_SOON_HOURS * HOUR_MS) {
    return {
      kind: "soon",
      label: `Due in ${Math.max(1, Math.round(left / HOUR_MS))}h`,
    };
  }
  const tomorrow = new Date(now.getTime() + DAY_MS).toDateString();
  return {
    kind: "ok",
    label:
      due.toDateString() === now.toDateString()
        ? `Due in ${Math.round(left / HOUR_MS)}h`
        : due.toDateString() === tomorrow
          ? "Due tomorrow"
          : `Due ${shortDate(due.toISOString())}`,
  };
};
